import { inject, Injectable } from '@angular/core';
import { deflate, inflate } from 'pako';
import { AccountStateService } from './account-state.service';

interface StoredValue {
  _storekey: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private _accountStateService = inject(AccountStateService);

  public storeElement(key: string, value: Object) {
    if (typeof localStorage === 'undefined') return false;

    try {
      const payload: StoredValue = { ...value, _storekey: this._accountStateService.$accountHash() };
      const json = JSON.stringify(payload);
      const compressed = deflate(json, { level: 3 });
      const encoded = LocalStorageService._toBase64(compressed);
      localStorage.setItem(key, encoded);
      return true;
    } catch (err) {
      console.error('[LocalStorageService] storeElement error:', err);
      return false;
    }
  }

  /**
   * Encodes bytes to base64 in chunks: a spread (`String.fromCharCode(...bytes)`)
   * passes one argument per byte and blows the call stack on large payloads
   * (accounts with several thousand devices).
   */
  private static _toBase64(bytes: Uint8Array): string {
    const CHUNK_SIZE = 0x8000;
    let binary = '';

    for (let offset = 0; offset < bytes.length; offset += CHUNK_SIZE) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK_SIZE));
    }

    return btoa(binary);
  }

  public static getElement(key: string): any {
    if (typeof localStorage === 'undefined') return null;

    try {
      const accountHash = localStorage.getItem('accountHash');
      const raw = localStorage.getItem(key);

      if (!raw) return null;

      const decoded = Uint8Array.from(atob(raw), c => c.charCodeAt(0));
      const decompressed = inflate(decoded, { toText: true });
      const parsed = JSON.parse(decompressed) as StoredValue;

      if (parsed['_storekey'] !== accountHash) {
        LocalStorageService.removeElement(key);
        console.debug('[LocalStorageService] cleared', key, '— account mismatch');
        return null;
      }

      return parsed;
    } catch (err) {
      console.warn('[LocalStorageService] getElement error, clearing key:', key, err);
      LocalStorageService.removeElement(key);
      return null;
    }
  }

  public static removeElement(key: string) {
    localStorage.removeItem(key);
  }

  public static clearAll(): void {
    localStorage.clear();
  }

  public static removeAllExcept(keysToKeep: string[]): void {
    if (typeof localStorage === 'undefined') return;

    for (const key in localStorage) {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    }
  }
}
